import store from '@/store/store';
import i18n from '@/i18n';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import { SOCIETE_ROLE_VENTE } from '@/configuration/societe/societe';
import genererDevisCommande from '@/services/devisCommandes/genererDevisCommande';
import {
  TYPE_COMMANDE,
  NORMAL,
  SENS_VENTE,
  POURCENTAGE,
  REGROUPEMENT,
  MODULE_GESTION_COMMERCIALE,
  LOCATION,
  LISTE_UNITES,
} from '@/configuration/devisConf';
import { CATEGORIE_UNITAIRE } from '@/configuration/catalogue/produitConf';
import { COMMERCIAL, DIRECTEUR_COMMERCIAL, ASSISTANT_COMMERCIAL } from '@/configuration/parametrage/utilisateurConf';
import { cloneDeep } from 'lodash';
import calculLigneCommandeNormal from '@/services/devisCommandes/calcul/calculLigneCommandeNormal';
import genererIntervenant from '@/services/devisCommandes/genererIntervenant';
import genererLigneCommande from '@/services/devisCommandes/genererLigneCommande';
import genererGuid from '@/services/general/genererGuid';
import calculArrondi from '@/services/calcul/calculArrondi';
import {
  isNoPropertie,
  isNameExist,
  fillEasyData,
  typeLignes,
  formatPourcentageToNumber,
  findConfrere,
  typeLigneDivers,
  formatDateExcel,
} from './services';

function generationLigneNormal(ligne, key, produits, ordre, ligneRegroupement, fournisseurs) {
  if (isNoPropertie(ligne[`referenceProduit${key}`])) return [];
  const produit = produits.find(p => p.reference.toLowerCase() === ligne[`referenceProduit${key}`].toLowerCase());
  const keyIndex = typeLignes.findIndex(k => k === key) + 1;
  const ligneNormal = genererLigneCommande(ordre);
  ligneNormal.ordre = ordre + !!ligneRegroupement ? keyIndex : 0;
  ligneNormal.produitId = produit.id;
  ligneNormal.libelle = produit.libelle;
  ligneNormal.type = NORMAL;
  ligneNormal.typeProduit = LOCATION;
  ligneNormal.typeRemise = POURCENTAGE;
  ligneNormal.quantite = calculArrondi(parseFloat(ligne[`quantite${key}`]), 10);
  ligneNormal.prixUnitaire = parseFloat(ligne[`prixUnitaire${key}`]);
  ligneNormal.uniteVente = LISTE_UNITES.find(u => i18n.t(u.libelle).replace('$$', '').toLowerCase() === ligne[`unite${key}`].toLowerCase()).code;
  ligneNormal.totalAvantRemise = ligne[`montantBrut${key}`];
  ligneNormal.remise = -1 * formatPourcentageToNumber(ligne[`tauxDeRemise${key}`]);
  ligneNormal.totalHtAvecLigneRemise = ligne[`montantNet${key}`];
  ligneNormal.tauxTva = parseFloat(ligne[`tauxTva${key}`]);
  if (isNoPropertie(ligne[`fournisseur${key}`])) {
    ligneNormal.fournisseurId = ligneRegroupement?.fournisseurId;
  } else {
    const confrere = findConfrere(fournisseurs, ligne[`fournisseur${key}`]);
    ligneNormal.fournisseurId = confrere?.id;
  }
  if (!isNoPropertie(ligne[`montantPremiereEcheance${key}`])) {
    ligneNormal.montantPremiereEcheance = parseFloat(ligne[`montantPremiereEcheance${key}`]);
    if (ligneNormal.montantPremiereEcheance !== 0) {
      ligneNormal.referenceEcheancier = genererGuid();
    }
  }
  calculLigneCommandeNormal(ligneNormal);
  return [ligneNormal];
}

function generationLigneRegroupement(ligne, index, produits, fournisseurs) {
  const ligneRegroupement = genererLigneCommande((index + 1));

  let produit = null;
  if (isNoPropertie(ligne.produit)) {
    produit = produits.find(
      p => p.reference.toLowerCase() === ligne.produitReference.toLowerCase(),
    );
  } else {
    produit = produits.find(p => p.reference.toLowerCase() === ligne.produit.toLowerCase());
  }

  ligneRegroupement.produitId = produit?.id;
  ligneRegroupement.libelle = produit?.libelle;
  ligneRegroupement.type = REGROUPEMENT;
  ligneRegroupement.typeProduit = CATEGORIE_UNITAIRE;
  ligneRegroupement.typeRemise = POURCENTAGE;
  ligneRegroupement.dateDebut = formatDateExcel(ligne.dateDebut);
  ligneRegroupement.dateFin = formatDateExcel(ligne.dateFin);
  ligneRegroupement.quantiteDispo = 1;

  if (isNoPropertie(ligne.confrere)) {
    ligneRegroupement.fournisseurId = produit.tiersId;
  } else {
    const confrere = findConfrere(fournisseurs, ligne.confrere);
    ligneRegroupement.fournisseurId = confrere?.id;
  }

  let newlignesCommande = [];
  typeLignes.forEach(type => {
    newlignesCommande = newlignesCommande.concat(
      generationLigneNormal(
        ligne,
        type,
        produits,
        ligneRegroupement.ordre,
        ligneRegroupement,
        fournisseurs,
      ),
    );
  });
  ligneRegroupement.lignesCommande = newlignesCommande;
  return ligneRegroupement;
}

export function fillCommandeWithData(
  commandeToCreate,
  commande,
  intervenants,
  lignesCommande,
  clients,
  produits,
  fournisseurs,
) {
  const societe = store.getters['societe/societeActive'];
  const users = store.getters['parametres/parametre'](PARAMETRES_KEYS.utilisateur);

  const banColumnAuto = ['role', 'commercial', 'assistant', 'agence', 'echeancierDateDebut', 'echeancierDateFin', 'echeancierDateFinPremiereEcheance', 'echeancierMontant'];
  const intColumn = ['montantHt', 'montantTva', 'montantTtc'];
  const boolColumn = ['renouvelable', 'reconductible'];

  fillEasyData(commandeToCreate, commande, banColumnAuto, intColumn, boolColumn);

  // Gestion des autres champs commande
  commandeToCreate.categorieVente = CATEGORIE_UNITAIRE;

  commandeToCreate.role = SOCIETE_ROLE_VENTE.find(r => i18n.t(r.libelle).replace('$$', '').toLowerCase() === commande.role.toLowerCase())?.code;

  commandeToCreate.commercialId = users.find(
    u => [COMMERCIAL, DIRECTEUR_COMMERCIAL].includes(u.profil)
        && isNameExist(commande.commercial, u.nom, u.prenom),
  )?.id;

  commandeToCreate.assistantId = users.find(
    u => u.profil === ASSISTANT_COMMERCIAL && isNameExist(commande.assistant, u.nom, u.prenom),
  )?.id;

  commandeToCreate.agenceId = societe.agences.find(
    a => a.libelle.toLowerCase() === commande.agence.toLowerCase(),
  )?.id;

  // Gestion des intervenants
  const banAutoInter = ['repartition', 'codeCommande', 'codeTiers', 'referenceExterne', 'nomCommercial'];
  const boolInter = ['payeur', 'annonceur', 'mandataire', 'donneurOrdre', 'nvClient'];
  commandeToCreate.intervenants = intervenants.map(intervenant => {
    let tiers = null;
    if (intervenant.codeTiers) {
      tiers = clients.find(t => t.code === intervenant.codeTiers);
    } else if (intervenant.referenceExterne) {
      tiers = clients.find(t => t.referenceExterne === intervenant.referenceExterne);
    } else if (intervenant.nomCommercial) {
      tiers = clients.find(
        t => t.nomCommercial.trim().toLowerCase()
        === intervenant.nomCommercial.trim().toLowerCase(),
      );
    }
    const intervenantToCreate = cloneDeep(genererIntervenant());
    fillEasyData(intervenantToCreate, intervenant, banAutoInter, [], boolInter);
    intervenantToCreate.repartition = formatPourcentageToNumber(intervenant.repartition);
    intervenantToCreate.tiersId = tiers.id;
    intervenantToCreate.tiers = tiers;
    intervenantToCreate.nomCommercial = tiers.nomCommercial;
    intervenantToCreate.adressePostale = tiers.adressePostale;
    intervenantToCreate.delaiReglement = tiers.delaiReglement;
    intervenantToCreate.modeReglement = tiers.modeReglement;
    return intervenantToCreate;
  });

  // generation des lignes de commandes
  commandeToCreate.lignesCommande = lignesCommande.map((ligne, index) => {
    if (isNoPropertie(ligne.produit) && isNoPropertie(ligne.produitReference)) {
      return typeLigneDivers.map(key => generationLigneNormal(
        ligne, key, produits, index * 100, null, fournisseurs,
      ));
    }
    return generationLigneRegroupement(
      ligne,
      index,
      produits,
      fournisseurs,
    );
  }).flat(2);

  return commandeToCreate;
}

export function genererCommande(
  commande,
  intervenant,
  lignesCommande,
  clients,
  produits,
  fournisseurs,
) {
  let commandeToCreate = genererDevisCommande(TYPE_COMMANDE, NORMAL, store.getters['societe/societeActive'], SENS_VENTE, MODULE_GESTION_COMMERCIALE);
  commandeToCreate.societeId = store.getters['societe/societeActiveId'];
  commandeToCreate = fillCommandeWithData(
    commandeToCreate,
    commande,
    intervenant,
    lignesCommande,
    clients,
    produits,
    fournisseurs,
  );
  return commandeToCreate;
}
